import { api } from '../console/api.ts';
import { type CarrierView, carrierName } from '../console/carriers.ts';
import { field, pill, skel } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, fmt, icon, type Money, REASON, when } from '../console/format.ts';
import { endorsementScope, followRequest } from '../console/retry-keys.ts';
import { type Actions, current, render } from '../console/router.ts';
import { act } from '../console/ui.ts';
import {
  dateControl,
  moneyControl,
  numberControl,
  selectControl,
  textareaControl,
  textControl,
} from '../controls.ts';

/**
 * A policy's endorsements and cancellation (#311, ADR-0036), apart from «Pólizas y recibos» so the
 * policy's detail (#310) uses the same panels: the endorsement panel (sum insured, GPS, vehicle,
 * beneficiaries), the cancellation panel with its refund previewed before confirming (the
 * direction cancels; the agent asks for it), the pending request with its approval or rejection,
 * and the history of endorsements and cancellation.
 */

/** A policy's period as these panels need it (a «Pólizas y recibos» card). */
export type PolicyRef = {
  id: string;
  carrier: string;
  productLine: string;
  policyNumber: string;
  currency: string;
};

// ------------------------------------------------------------------ endorsements

export const ENDORSEMENT_TYPE: Record<string, string> = {
  CHANGE_SUM_INSURED: 'Suma asegurada',
  SET_TRACKER: 'GPS instalado',
  CHANGE_VEHICLE: 'Cambio de vehículo',
  CHANGE_BENEFICIARIES: 'Cambio de beneficiarios',
};
/** What each product line can change, in this order (the first one is the default). */
const BY_LINE: Record<string, string[]> = {
  AUTO: ['CHANGE_SUM_INSURED', 'SET_TRACKER', 'CHANGE_VEHICLE'],
  LIFE: ['CHANGE_SUM_INSURED', 'CHANGE_BENEFICIARIES'],
  PERSONAL_ACCIDENT: ['CHANGE_SUM_INSURED', 'CHANGE_BENEFICIARIES'],
};
const BODY: Record<string, string> = {
  SEDAN: 'Sedán',
  HATCHBACK: 'Hatchback',
  SUV: 'Camioneta (SUV)',
  PICKUP: 'Pick-up',
  LIGHT_TRUCK: 'Camión liviano',
  MOTORCYCLE: 'Motocicleta',
};
const RELATIONSHIP: Record<string, string> = {
  SPOUSE: 'Cónyuge',
  CHILD: 'Hijo o hija',
  PARENT: 'Padre o madre',
  SIBLING: 'Hermano o hermana',
  OTHER: 'Otro',
};
const STATUS: Record<string, [string, string]> = {
  IN_REVIEW: ['warn', 'En revisión'],
  APPLIED: ['ok', 'Aplicado'],
  DECLINED: ['bad', 'Rechazado'],
};

/**
 * The changes the console offers for a policy: those of its product line, plus whatever else
 * the carrier's integration declares. `byHand`: the carrier's integration does not take it, so it
 * goes to the carrier's desk as a work order (ADR-0017).
 */
export function endorsementOptions(carrier: CarrierView | undefined, productLine: string) {
  const declared = (carrier?.endorse?.[productLine] ?? []).filter((t) => t in ENDORSEMENT_TYPE);
  const types = [...new Set([...(BY_LINE[productLine] ?? []), ...declared])];
  return types.map((type) => ({ type, byHand: !declared.includes(type) }));
}

/** «Los porcentajes deben sumar 100 %» when they do not (null when they do). */
export function beneficiariesError(shares: number[]): string | null {
  if (!shares.length) return 'Escribe al menos un beneficiario.';
  if (shares.some((s) => !Number.isInteger(s) || s < 1 || s > 100))
    return 'Cada porcentaje es un número entero de 1 a 100.';
  const total = shares.reduce((a, b) => a + b, 0);
  return total === 100 ? null : `Los porcentajes deben sumar 100 % (ahora suman ${total} %).`;
}

const beneficiaryRow = (n: number) => `<div class="bene-row" data-bene-row>
  ${field(`Beneficiario ${n}`, textControl({ 'data-bene-name': true, maxlength: 120, autocomplete: 'off' }))}
  ${field(
    'Parentesco',
    selectControl(
      { 'data-bene-rel': true },
      Object.entries(RELATIONSHIP).map(([value, label]) => ({ value, label })),
    ),
  )}
  ${field('Porcentaje', numberControl({ 'data-bene-share': true, min: 1, max: 100, steppers: false }))}
</div>`;

/** The endorsement panel of a policy (hidden until «Endosar» opens it). */
export function endorsePanel(
  s: PolicyRef,
  options: { type: string; byHand: boolean }[],
  effective: string,
) {
  const first = options[0]?.type ?? '';
  const sum = s.currency === 'USD' ? 'US$' : 'Q';
  const fields = (type: string, html: string) =>
    options.some((o) => o.type === type)
      ? `<div class="endorse-fields" data-endorse-fields="${type}"${type === first ? '' : ' hidden'}>${html}</div>`
      : '';
  const byHand = options.find((o) => o.type === first)?.byHand ?? false;
  return `<div class="policy-panel endorse-panel" data-endorse-panel="${esc(s.id)}" data-currency="${esc(s.currency)}" hidden>
    <div class="toolbar">
      <div class="grow"><strong>Endoso</strong><span class="muted small">La aseguradora calcula la prima; su cobro sigue la configuración de ${esc(carrierName(s.carrier))}.</span></div>
      ${field(
        'Cambio',
        selectControl(
          { 'data-endorse-type': true },
          options.map((o) => ({
            value: o.type,
            label: `${ENDORSEMENT_TYPE[o.type] ?? o.type}${o.byHand ? ' (por orden de trabajo)' : ''}`,
          })),
        ),
      )}
      ${field('Desde', dateControl({ 'data-endorse-date': true, value: effective }))}
    </div>
    ${fields('CHANGE_SUM_INSURED', `<div class="toolbar">${field('Nueva suma asegurada', moneyControl({ 'data-endorse-sum': true, placeholder: '180000.00', currency: sum }))}</div>`)}
    ${fields('SET_TRACKER', '<p class="note info">El vehículo queda con GPS instalado desde esa fecha.</p>')}
    ${fields(
      'CHANGE_VEHICLE',
      `<div class="toolbar endorse-grid">
        ${field('Marca', textControl({ 'data-veh-make': true, maxlength: 40, placeholder: 'HYUNDAI' }))}
        ${field('Línea', textControl({ 'data-veh-model': true, maxlength: 40, placeholder: 'TUCSON' }))}
        ${field('Modelo (año)', numberControl({ 'data-veh-year': true, min: 1980, max: 2100, steppers: false, placeholder: '2025' }))}
        ${field(
          'Tipo',
          selectControl(
            { 'data-veh-body': true },
            Object.entries(BODY).map(([value, label]) => ({ value, label })),
          ),
        )}
        ${field(
          'Uso',
          selectControl({ 'data-veh-use': true }, [
            { value: 'PRIVATE', label: 'Particular' },
            { value: 'COMMERCIAL', label: 'Comercial' },
          ]),
        )}
        ${field('Placa', textControl({ 'data-veh-plate': true, maxlength: 12, placeholder: 'P-456DEF', autocomplete: 'off' }))}
        ${field('Valor asegurado', moneyControl({ 'data-veh-value': true, placeholder: '250000.00', currency: sum }))}
      </div>`,
    )}
    ${fields(
      'CHANGE_BENEFICIARIES',
      `<div class="bene-list" data-bene-list>${beneficiaryRow(1)}${beneficiaryRow(2)}</div>
      <div class="toolbar bene-foot"><button type="button" class="btn sm" data-bene-add>Agregar beneficiario</button><span class="muted small" data-bene-total aria-live="polite">Los porcentajes deben sumar 100 %.</span></div>`,
    )}
    <p class="note info" data-endorse-byhand${byHand ? '' : ' hidden'}>${icon('file')}<span>${esc(carrierName(s.carrier))} no recibe este cambio por su sistema: Nubi le abre una orden de trabajo y el endoso queda en el historial cuando la complete.</span></p>
    <div class="toolbar endorse-send"><p class="field-error grow" role="alert" data-endorse-error></p><button type="button" class="btn primary" data-endorse-send="${esc(s.id)}" data-currency="${esc(s.currency)}">Enviar endoso</button></div>
  </div>`;
}

const value = (root: ParentNode, sel: string) =>
  (root.querySelector<HTMLInputElement>(sel)?.value ?? '').trim();
const amount = (raw: string) => Number(raw.replace(/[Q$US,\s]/gi, ''));

/** The change the panel describes, as the API takes it (or what is missing, for the person). */
export function readChange(panel: HTMLElement, currencyRaw: string | undefined) {
  const type = value(panel, '[data-endorse-type]');
  const currency = currencyRaw === 'USD' ? 'USD' : 'GTQ';
  const money = (raw: string, example: string, what: string) => {
    const n = amount(raw);
    if (!raw || !Number.isFinite(n) || n <= 0)
      throw new InputError(`Escribe ${what}, por ejemplo ${example}`);
    return { amountMinor: Math.round(n * 100), currency };
  };
  if (type === 'CHANGE_SUM_INSURED')
    return {
      type,
      sumInsured: money(value(panel, '[data-endorse-sum]'), '180000.00', 'la nueva suma asegurada'),
    };
  if (type === 'CHANGE_VEHICLE') {
    const make = value(panel, '[data-veh-make]').toUpperCase();
    const model = value(panel, '[data-veh-model]').toUpperCase();
    const year = Number(value(panel, '[data-veh-year]'));
    if (!make || !model) throw new InputError('Escribe la marca y la línea del vehículo nuevo.');
    if (!Number.isInteger(year) || year < 1980 || year > 2100)
      throw new InputError('Escribe el modelo (año) del vehículo nuevo, por ejemplo 2025.');
    const plate = value(panel, '[data-veh-plate]').toUpperCase();
    return {
      type,
      vehicle: {
        make,
        model,
        modelYear: year,
        bodyType: value(panel, '[data-veh-body]') || 'SEDAN',
        use: value(panel, '[data-veh-use]') || 'PRIVATE',
        insuredValue: money(value(panel, '[data-veh-value]'), '250000.00', 'el valor asegurado'),
        hasTracker: false,
        ...(plate ? { plate } : {}),
      },
    };
  }
  if (type === 'CHANGE_BENEFICIARIES') {
    const rows = [...panel.querySelectorAll<HTMLElement>('[data-bene-row]')]
      .map((r) => ({
        fullName: value(r, '[data-bene-name]'),
        relationship: value(r, '[data-bene-rel]') || 'OTHER',
        share: value(r, '[data-bene-share]'),
      }))
      .filter((r) => r.fullName || r.share);
    if (rows.some((r) => r.fullName.length < 3))
      throw new InputError('Escribe el nombre completo de cada beneficiario.');
    const error = beneficiariesError(rows.map((r) => Number(r.share)));
    if (error) throw new InputError(error);
    return {
      type,
      beneficiaries: rows.map((r) => ({
        fullName: r.fullName,
        relationship: r.relationship,
        sharePercent: Number(r.share),
      })),
    };
  }
  return { type: 'SET_TRACKER', installed: true };
}

/** Shows the fields of the change chosen (and whether it goes by work order). */
function showFields(select: HTMLSelectElement) {
  const panel = select.closest<HTMLElement>('[data-endorse-panel]');
  if (!panel) return;
  for (const f of panel.querySelectorAll<HTMLElement>('[data-endorse-fields]'))
    f.hidden = f.dataset.endorseFields !== select.value;
  const byHand = /\(por orden de trabajo\)$/.test(
    select.querySelector(`[value="${CSS.escape(select.value)}"]`)?.textContent ?? '',
  );
  const note = panel.querySelector<HTMLElement>('[data-endorse-byhand]');
  if (note) note.hidden = !byHand;
  const error = panel.querySelector<HTMLElement>('[data-endorse-error]');
  if (error) error.textContent = '';
}

/** «Suman 100 %» as the person types the shares. */
function showTotal(panel: HTMLElement) {
  const shares = [...panel.querySelectorAll<HTMLInputElement>('[data-bene-share]')]
    .map((i) => i.value.trim())
    .filter(Boolean)
    .map(Number);
  const out = panel.querySelector<HTMLElement>('[data-bene-total]');
  if (!out) return;
  const total = shares.reduce((a, b) => a + b, 0);
  out.textContent = shares.length
    ? total === 100
      ? 'Suman 100 %.'
      : `Suman ${total} %: deben sumar 100 %.`
    : 'Los porcentajes deben sumar 100 %.';
  out.classList.toggle('bad-text', shares.length > 0 && total !== 100);
}

// ------------------------------------------------------------------ cancellation

/** A cancellation request (GET /v1/cancellation-requests, the policy's detail). */
export type CancellationRequestView = {
  id: string;
  policyId: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason: string;
  effectiveDate: string;
  note: string | null;
  requestedByName: string | null;
  requestedAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
};

export const CANCELLATION_REASON: Record<string, string> = {
  CUSTOMER_REQUEST: 'A solicitud del cliente',
  RISK_TRANSFERRED: 'Riesgo transferido (vendió el vehículo o el bien)',
  NON_PAYMENT: 'Falta de pago',
};
/** The reason inside a sentence. */
const REASON_IN_TEXT: Record<string, string> = {
  CUSTOMER_REQUEST: 'a solicitud del cliente',
  RISK_TRANSFERRED: 'riesgo transferido',
  NON_PAYMENT: 'falta de pago',
};
const METHOD: Record<string, string> = {
  PRO_RATA: 'a prorrata por días',
  SHORT_RATE: 'a corto plazo',
  FREE_LOOK: 'dentro del período de retracto',
  NO_REFUND: 'sin devolución',
};

/**
 * The cancellation panel (hidden until opened): why and from when, the refund the carrier
 * previews, and the confirmation. `mode` CANCEL (the direction cancels) or REQUEST (the agent
 * asks the direction, with an optional note).
 */
export function cancelPanel(s: PolicyRef, mode: 'CANCEL' | 'REQUEST', effective: string) {
  return `<div class="policy-panel cancel-panel" data-cancel-panel="${esc(s.id)}" data-mode="${mode}" hidden>
    <div class="toolbar">
      <div class="grow"><strong>${mode === 'CANCEL' ? 'Cancelar la póliza' : 'Solicitar la cancelación'}</strong><span class="muted small">${
        mode === 'CANCEL'
          ? 'Se cancela con la aseguradora: los recibos que vencen desde esa fecha quedan anulados.'
          : 'La dirección de la agencia la revisa; la póliza sigue vigente hasta que la apruebe.'
      }</span></div>
      ${field(
        'Motivo',
        selectControl(
          { 'data-cancel-reason': s.id },
          Object.entries(CANCELLATION_REASON).map(([value, label]) => ({ value, label })),
        ),
      )}
      ${field('Desde', dateControl({ 'data-cancel-date': s.id, value: effective }))}
    </div>
    ${mode === 'REQUEST' ? `<div class="toolbar">${field('Nota para la dirección (opcional)', textareaControl({ 'data-cancel-note': true, maxlength: 500, rows: 2 }))}</div>` : ''}
    <div class="cancel-preview" data-cancel-preview="${esc(s.id)}" aria-live="polite"></div>
    <div class="toolbar cancel-send"><span class="grow"></span>${
      mode === 'CANCEL'
        ? `<button type="button" class="btn danger" data-cancel-send="${esc(s.id)}">Confirmar cancelación</button>`
        : `<button type="button" class="btn primary" data-cancel-ask="${esc(s.id)}">Enviar solicitud</button>`
    }</div>
  </div>`;
}

type CancelResult = {
  method: string;
  premiumPaid: Money;
  earnedPremium: Money;
  fee: Money;
  refund: Money;
  commissionClawback: Money;
};

/** The carrier's figures of a cancellation (previewed or done), as a small table. */
export function cancellationFigures(r: Partial<Record<keyof CancelResult, Money | string | null>>) {
  const row = (label: string, m: unknown, strong = false) =>
    m && typeof m === 'object'
      ? `<tr><th scope="row">${label}</th><td class="num">${strong ? `<strong>${fmt(m as Money)}</strong>` : fmt(m as Money)}</td></tr>`
      : '';
  return `<table class="table figures"><tbody>
    ${row('Prima pagada', r.premiumPaid)}
    ${row('Prima devengada (la aseguradora la conserva)', r.earnedPremium)}
    ${r.fee && typeof r.fee === 'object' && (r.fee as Money).amountMinor ? row('Cargo por cancelación', r.fee) : ''}
    ${row('Devolución al cliente', r.refund, true)}
    ${row('Comisión que devuelve la agencia', r.commissionClawback)}
  </tbody></table>`;
}

/** Asks the carrier what cancelling would give, and shows it in the panel. */
function previewInto(panel: HTMLElement) {
  const out = panel.querySelector<HTMLElement>('[data-cancel-preview]');
  if (!out) return Promise.resolve();
  return paintPreview(
    out,
    panel.dataset.cancelPanel ?? '',
    value(panel, '[data-cancel-reason]'),
    value(panel, '[data-cancel-date]'),
  );
}

/** The carrier's preview of cancelling a policy for a reason from a date, painted in `out`. */
async function paintPreview(out: HTMLElement, id: string, reason: string, effectiveDate: string) {
  if (!reason || !effectiveDate) return;
  out.innerHTML = `<div class="toolbar">${skel(14, '50%')}</div>`;
  out.dataset.asked = `${reason}|${effectiveDate}`;
  try {
    const r = await api<{ available: boolean; preview: CancelResult | null }>(
      'POST',
      `/v1/policies/${encodeURIComponent(id)}/cancellation-preview`,
      { reason, effectiveDate },
    );
    // Another reason or date was chosen meanwhile: that preview paints.
    if (out.dataset.asked !== `${reason}|${effectiveDate}`) return;
    out.innerHTML =
      r.available && r.preview
        ? `<div class="toolbar"><div class="grow"><strong>Vista previa de la aseguradora</strong><span class="muted small">Calculada ${esc(METHOD[r.preview.method] ?? r.preview.method)} al ${esc(date(effectiveDate))}; nada se cancela hasta confirmar.</span></div></div>${cancellationFigures(r.preview)}`
        : '<p class="note info">La aseguradora no da una vista previa: la devolución se conoce cuando cancela.</p>';
  } catch (e) {
    if (out.dataset.asked !== `${reason}|${effectiveDate}`) return;
    out.innerHTML = `<p class="note bad">${icon('warn')}<span>La aseguradora no acepta esa cancelación: ${esc(e instanceof Error ? e.message : String(e))}</span></p>`;
  }
}

/**
 * «Cancelación solicitada»: who asked, why and from when. The direction reviews the refund the
 * carrier previews and approves it (the carrier cancels), or rejects it with a note.
 */
export function pendingRequest(r: CancellationRequestView, s: PolicyRef, canDecide: boolean) {
  return `<div class="policy-panel cancel-request" data-cancel-request="${esc(r.id)}">
    <div class="toolbar"><div class="grow"><span>${pill('warn', 'Cancelación solicitada')}</span>
      <span class="small">${esc(r.requestedByName ?? 'La agente')} la pidió el ${esc(when(r.requestedAt))}: ${esc(REASON_IN_TEXT[r.reason] ?? r.reason)}, desde el ${esc(date(r.effectiveDate))}.</span>
      ${r.note ? `<span class="muted small">«${esc(r.note)}»</span>` : ''}</div>
    ${
      canDecide
        ? `<button type="button" class="btn" data-cancel-reject-open="${esc(r.id)}" aria-expanded="false">Rechazar</button><button type="button" class="btn primary" data-cancel-review="${esc(r.id)}" data-policy-id="${esc(s.id)}" data-reason="${esc(r.reason)}" data-date="${esc(r.effectiveDate)}" aria-expanded="false">Revisar la devolución</button>`
        : '<span class="muted small">La dirección de la agencia la revisará.</span>'
    }</div>
    ${
      canDecide
        ? `<div data-cancel-review-panel="${esc(r.id)}" hidden><div class="cancel-preview" data-review-preview aria-live="polite"></div>
      <div class="toolbar cancel-send"><span class="grow"></span><button type="button" class="btn danger" data-cancel-approve="${esc(r.id)}" data-policy-id="${esc(s.id)}">Aprobar y cancelar</button></div></div>
    <div class="toolbar" data-cancel-reject-panel="${esc(r.id)}" hidden>${field('Por qué se rechaza', textareaControl({ 'data-cancel-reject-note': true, maxlength: 500, rows: 2 }), { required: true })}<button type="button" class="btn" data-cancel-reject="${esc(r.id)}" data-policy-id="${esc(s.id)}">Rechazar solicitud</button></div>`
        : ''
    }
  </div>`;
}

// ------------------------------------------------------------------ history

type EndorsementView = {
  id: string;
  channel: 'INTEGRATION' | 'WORK_ORDER';
  status: string;
  carrierEndorsementRef: string | null;
  effectiveDate: string;
  changes: { type: string; before: unknown; after: unknown }[];
  premium: {
    net: Money;
    issuanceFee: Money | null;
    endorsementFee: Money | null;
    vat: Money | null;
    total: Money;
  } | null;
  commission: Money | null;
  declineReasons: string[];
  requestedAt: string;
};
/** What GET /v1/policies/:id says of the history (#311). */
export type PolicyHistoryView = {
  policyNumber: string;
  endorsements: EndorsementView[];
  cancellation:
    | (Partial<Record<keyof CancelResult, Money | string | null>> & {
        reason: string | null;
        effectiveDate: string | null;
        registeredOn: string | null;
        carrierCancellationRef: string | null;
      })
    | null;
  cancellationRequests: CancellationRequestView[];
};

type Vehicle = { make?: string; model?: string; modelYear?: number; plate?: string | null };
type Bene = { fullName: string; relationship: string; sharePercent: number };
const isMoney = (x: unknown): x is Money =>
  !!x && typeof x === 'object' && 'amountMinor' in (x as object);
/** One side of a change, readable: the vehicle, the beneficiaries, a sum, GPS. */
function sideOf(type: string, v: unknown): string {
  if (v === null || v === undefined) return '<span class="muted">sin dato</span>';
  if (type === 'CHANGE_VEHICLE') {
    const x = v as Vehicle;
    return `${esc([x.make, x.model, x.modelYear].filter(Boolean).join(' '))}${x.plate ? ` · placa <span class="mono">${esc(x.plate)}</span>` : ''}`;
  }
  if (type === 'CHANGE_BENEFICIARIES')
    return (v as Bene[]).map((b) => `${esc(b.fullName)} ${b.sharePercent} %`).join(', ');
  if (isMoney(v)) return fmt(v);
  if (typeof v === 'boolean') return v ? 'con GPS' : 'sin GPS';
  return esc(JSON.stringify(v));
}

/** A policy's history: its endorsements, its cancellation and the requests to cancel it. */
export function historyHtml(h: PolicyHistoryView) {
  const endorsements = h.endorsements
    .map((e) => {
      const [tone, label] = STATUS[e.status] ?? ['info', e.status];
      const p = e.premium;
      const breakdown = p
        ? `<div class="small">Neta ${fmt(p.net)}${p.issuanceFee ? ` · gastos ${fmt(p.issuanceFee)}` : ''}${p.endorsementFee?.amountMinor ? ` · endoso ${fmt(p.endorsementFee)}` : ''}${p.vat ? ` · IVA ${fmt(p.vat)}` : ''}</div><div><strong>Total ${fmt(p.total)}</strong>${e.commission ? ` <span class="muted small">· comisión ${fmt(e.commission)}</span>` : ''}</div>`
        : '<span class="muted small">Sin prima todavía</span>';
      return `<tr data-endorsement="${esc(e.id)}">
        <td><span class="mono">${esc(e.carrierEndorsementRef ?? '—')}</span><div class="muted small">desde el ${esc(date(e.effectiveDate))}${e.channel === 'WORK_ORDER' ? ' · por orden de trabajo' : ''}</div></td>
        <td>${e.changes
          .map(
            (c) =>
              `<div><strong>${esc(ENDORSEMENT_TYPE[c.type] ?? c.type)}</strong></div><div class="small change-sides"><span>${sideOf(c.type, c.before)}</span> <span aria-hidden="true">→</span><span class="sr-only">cambia a</span> <span>${sideOf(c.type, c.after)}</span></div>`,
          )
          .join('')}</td>
        <td class="num">${breakdown}</td>
        <td>${pill(tone, label)}${e.status === 'DECLINED' && e.declineReasons.length ? `<div class="muted small">${esc(e.declineReasons.map((r) => REASON[r] ?? r).join(', '))}</div>` : ''}</td></tr>`;
    })
    .join('');
  const c = h.cancellation;
  const cancellation = c
    ? `<section class="history-cancel" aria-label="Cancelación"><div class="toolbar"><div class="grow"><strong>${pill('CANCELLED', 'Cancelada')} desde el ${esc(c.effectiveDate ? date(c.effectiveDate) : 'una fecha que Nubi no guardó')}</strong>
        <span class="muted small">${esc(c.reason ? (CANCELLATION_REASON[c.reason] ?? c.reason) : 'Motivo no registrado')}${c.carrierCancellationRef ? ` · anulación <span class="mono">${esc(c.carrierCancellationRef)}</span>` : ''}${typeof c.method === 'string' ? ` · ${esc(METHOD[c.method] ?? c.method)}` : ''}</span></div></div>
        ${c.refund || c.premiumPaid || c.commissionClawback ? cancellationFigures(c) : '<p class="note info">La aseguradora no informó la devolución.</p>'}</section>`
    : '';
  const requests = h.cancellationRequests
    .filter((r) => r.status !== 'PENDING')
    .map(
      (r) =>
        `<li>${pill(r.status === 'APPROVED' ? 'ok' : 'bad', r.status === 'APPROVED' ? 'Solicitud aprobada' : 'Solicitud rechazada')} ${esc(r.requestedByName ?? 'La agente')} pidió cancelar (${esc(REASON_IN_TEXT[r.reason] ?? r.reason)}) el ${esc(when(r.requestedAt))}${r.decidedAt ? `; ${esc(r.decidedByName ?? 'la dirección')} decidió el ${esc(when(r.decidedAt))}` : ''}${r.decisionNote ? `: «${esc(r.decisionNote)}»` : ''}</li>`,
    )
    .join('');
  if (!endorsements && !cancellation && !requests)
    return `<p class="history-empty muted">${icon('file')}La póliza ${esc(h.policyNumber)} no tiene endosos ni cancelaciones todavía.</p>`;
  return `${
    endorsements
      ? `<div class="table-wrap" data-component="table"><table class="table history"><caption class="sr-only">Endosos de la póliza ${esc(h.policyNumber)}</caption><thead><tr><th>Endoso</th><th>Qué cambió</th><th class="num">Prima del endoso</th><th>Estado</th></tr></thead><tbody>${endorsements}</tbody></table></div>`
      : ''
  }${cancellation}${requests ? `<ul class="history-requests">${requests}</ul>` : ''}`;
}

/** Re-paints the list and opens the history of a policy again (after an action changed it). */
async function refresh() {
  await render(current, { quiet: true });
}

// ------------------------------------------------------------------ actions

export const actions: Actions = {
  changeData: {
    endorseType: (select) => showFields(select),
    cancelReason: (select) => {
      const panel = select.closest<HTMLElement>('[data-cancel-panel]');
      if (panel) void previewInto(panel);
    },
    cancelDate: (control) => {
      const panel = control.closest<HTMLElement>('[data-cancel-panel]');
      if (panel) void previewInto(panel);
    },
  },
  inputData: {
    beneShare: (control) => {
      const panel = control.closest<HTMLElement>('[data-endorse-panel]');
      if (panel) showTotal(panel);
    },
  },
  click: {
    beneAdd: (t) => {
      const list = t
        .closest<HTMLElement>('[data-endorse-panel]')
        ?.querySelector('[data-bene-list]');
      if (!list) return;
      const n = list.querySelectorAll('[data-bene-row]').length + 1;
      if (n > 10) return;
      list.insertAdjacentHTML('beforeend', beneficiaryRow(n));
      list.querySelector<HTMLElement>('[data-bene-row]:last-child [data-bene-name]')?.focus();
    },
    endorseSend: (t, d) => {
      if (!d.endorseSend) return;
      const panel = t.closest<HTMLElement>('[data-endorse-panel]');
      if (!panel) return;
      const error = panel.querySelector<HTMLElement>('[data-endorse-error]');
      if (error) error.textContent = '';
      let change: unknown;
      try {
        change = readChange(panel, d.currency);
      } catch (e) {
        // Said next to the button (and announced), before anything reaches the carrier.
        if (error) error.textContent = e instanceof Error ? e.message : String(e);
        return;
      }
      const effectiveDate = value(panel, '[data-endorse-date]');
      void act(t, 'Enviando el endoso a la aseguradora…', async () => {
        type Endorsed = {
          status: string;
          premium: { total: Money } | null;
          reasons: { category: string }[];
          billed: { mode: string; added: number | null; adjusted: unknown[] } | null;
          message?: string;
        };
        const schedule = d.endorseSend ?? '';
        const body = { effectiveDate, changes: [change] };
        // Sending the same endorsement again while the carrier reviews it (REFERRED) follows
        // that same request (the same key), never files another one (#219).
        const r = await followRequest(
          endorsementScope(schedule, body),
          (idempotencyKey) =>
            api<Endorsed>('POST', `/v1/schedules/${encodeURIComponent(schedule)}/endorse`, body, {
              idempotencyKey,
            }),
          (e) => e.status !== 'REFERRED',
        );
        await refresh();
        if (r.status === 'WORK_ORDER')
          return r.message ?? 'Orden de trabajo abierta: la aseguradora hará el endoso a mano.';
        if (r.status === 'REFERRED')
          return 'La aseguradora revisará el endoso; te avisaremos cuando responda.';
        if (r.status === 'DECLINED')
          throw new InputError(
            `La aseguradora rechazó el endoso: ${r.reasons.map((x) => REASON[x.category] ?? x.category).join(', ') || 'sin motivo'}.`,
          );
        const total = r.premium?.total;
        const how =
          r.billed?.mode === 'ADDITIONAL_INSTALLMENT'
            ? `en el recibo ${r.billed.added}`
            : r.billed?.mode === 'SPREAD_OVER_REMAINING' || r.billed?.mode === 'REDUCE_REMAINING'
              ? 'repartida en los recibos pendientes'
              : total?.amountMinor
                ? 'la cobra la aseguradora'
                : 'sin cambio de prima';
        return total?.amountMinor
          ? `Endoso aplicado: ${total.amountMinor < 0 ? 'devolución' : 'prima'} de ${fmt({ ...total, amountMinor: Math.abs(total.amountMinor) })}, ${how}.`
          : 'Endoso aplicado, sin cambio de prima.';
      });
    },
    cancel: (t, d) => {
      if (!d.cancel) return;
      togglePanel(t, d.cancel);
    },
    cancelRequestOpen: (t, d) => {
      if (!d.cancelRequestOpen) return;
      togglePanel(t, d.cancelRequestOpen);
    },
    cancelSend: (t, d) => {
      const id = d.cancelSend ?? '';
      const panel = t.closest<HTMLElement>('[data-cancel-panel]');
      if (!id || !panel) return;
      const body = {
        reason: value(panel, '[data-cancel-reason]'),
        effectiveDate: value(panel, '[data-cancel-date]'),
      };
      void act(t, 'Cancelando con la aseguradora…', async () => {
        const r = await api<{ status?: string; message?: string; refund?: Money }>(
          'POST',
          `/v1/schedules/${encodeURIComponent(id)}/cancel`,
          body,
        );
        await refresh();
        if (r.status === 'WORK_ORDER')
          return r.message ?? 'Orden de trabajo abierta: la aseguradora cancelará a mano.';
        return `Póliza cancelada${r.refund ? `: devolución de ${fmt(r.refund)}` : ''}; los recibos futuros quedaron anulados.`;
      });
    },
    cancelAsk: (t, d) => {
      const id = d.cancelAsk ?? '';
      const panel = t.closest<HTMLElement>('[data-cancel-panel]');
      if (!id || !panel) return;
      const note = value(panel, '[data-cancel-note]');
      const body = {
        reason: value(panel, '[data-cancel-reason]'),
        effectiveDate: value(panel, '[data-cancel-date]'),
        ...(note ? { note } : {}),
      };
      void act(t, 'Enviando la solicitud a la dirección…', async () => {
        await api('POST', `/v1/policies/${encodeURIComponent(id)}/cancellation-requests`, body);
        await refresh();
        return 'Cancelación solicitada: la dirección de la agencia la revisará.';
      });
    },
    cancelApprove: (t, d) => {
      const id = d.policyId ?? '';
      const request = d.cancelApprove ?? '';
      if (!id || !request) return;
      void act(t, 'Cancelando con la aseguradora…', async () => {
        const r = await api<{
          result: { status?: string; message?: string; refund?: Money } | null;
        }>(
          'POST',
          `/v1/policies/${encodeURIComponent(id)}/cancellation-requests/${encodeURIComponent(request)}/approve`,
          {},
        );
        await refresh();
        if (r.result?.status === 'WORK_ORDER')
          return r.result.message ?? 'Solicitud aprobada: la aseguradora cancelará a mano.';
        return `Solicitud aprobada y póliza cancelada${r.result?.refund ? `: devolución de ${fmt(r.result.refund)}` : ''}.`;
      });
    },
    cancelReview: (t, d) => {
      const panel = document.querySelector<HTMLElement>(
        `[data-cancel-review-panel="${CSS.escape(d.cancelReview ?? '')}"]`,
      );
      const out = panel?.querySelector<HTMLElement>('[data-review-preview]');
      if (!panel || !out) return;
      panel.hidden = !panel.hidden;
      t.setAttribute('aria-expanded', String(!panel.hidden));
      if (!panel.hidden) void paintPreview(out, d.policyId ?? '', d.reason ?? '', d.date ?? '');
    },
    cancelRejectOpen: (t, d) => {
      const panel = document.querySelector<HTMLElement>(
        `[data-cancel-reject-panel="${CSS.escape(d.cancelRejectOpen ?? '')}"]`,
      );
      if (!panel) return;
      panel.hidden = !panel.hidden;
      t.setAttribute('aria-expanded', String(!panel.hidden));
      if (!panel.hidden) panel.querySelector<HTMLElement>('[data-cancel-reject-note]')?.focus();
    },
    cancelReject: (t, d) => {
      const id = d.policyId ?? '';
      const request = d.cancelReject ?? '';
      const panel = t.closest<HTMLElement>('[data-cancel-reject-panel]');
      if (!id || !request || !panel) return;
      const note = value(panel, '[data-cancel-reject-note]');
      void act(t, 'Rechazando la solicitud…', async () => {
        if (!note) throw new InputError('Escribe por qué se rechaza la solicitud.');
        await api(
          'POST',
          `/v1/policies/${encodeURIComponent(id)}/cancellation-requests/${encodeURIComponent(request)}/reject`,
          { note },
        );
        await refresh();
        return 'Solicitud rechazada: la póliza sigue vigente.';
      });
    },
    historyRetry: (t, d) => {
      const panel = t.closest<HTMLElement>('[data-history-panel]');
      if (panel && d.historyRetry) void loadHistory(panel, d.historyRetry);
    },
    historyOpen: (t, d) => {
      const id = d.historyOpen ?? '';
      const panel = document.querySelector<HTMLElement>(`[data-history-panel="${CSS.escape(id)}"]`);
      if (!panel) return;
      panel.hidden = !panel.hidden;
      t.setAttribute('aria-expanded', String(!panel.hidden));
      if (panel.hidden) return;
      void loadHistory(panel, id);
    },
  },
};

/** Opens (or closes) a cancellation panel and previews the refund on opening. */
function togglePanel(t: HTMLElement, id: string) {
  const panel = document.querySelector<HTMLElement>(`[data-cancel-panel="${CSS.escape(id)}"]`);
  if (!panel) return;
  panel.hidden = !panel.hidden;
  t.setAttribute('aria-expanded', String(!panel.hidden));
  if (!panel.hidden) void previewInto(panel);
}

/** Loads a policy's history into its panel (with the four states). */
export async function loadHistory(panel: HTMLElement, id: string) {
  panel.innerHTML = `<div class="toolbar">${skel(14, '40%')}</div>`;
  try {
    const h = await api<PolicyHistoryView>('GET', `/v1/policies/${encodeURIComponent(id)}`);
    panel.innerHTML = `<div class="toolbar"><div class="grow"><strong>Historial de endosos y cancelación</strong></div></div>${historyHtml(h)}`;
  } catch {
    panel.innerHTML = `<p class="note bad">${icon('warn')}<span>No se pudo leer el historial de la póliza.</span> <button type="button" class="btn sm" data-history-retry="${esc(id)}">Reintentar</button></p>`;
  }
}
